'use client';

import { useSession } from 'next-auth/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePathname } from 'next/navigation';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Icon,
} from 'glic-ui-components/esm';
import { faArrowRight } from 'glic-ui-components/icons';
import { Button } from './ui/button';
import { Link } from './ui/link';

type QuickLinkItem = {
  label: string;
  dataTa: string;
  href?: string;
  onClick?: () => void;
};

export default function QuickLinksAside() {
  const { hideRetrieveASavedQuote, seismicLink } = useFlags();
  const { data } = useSession();
  const showRequestAdditionalStates = data?.user?.showRequestAdditionalStates;

  const pathname = usePathname();

  const quickLinks: QuickLinkItem[] = [
    ...(!hideRetrieveASavedQuote
      ? [
          {
            label: 'Retrieve a saved quote',
            dataTa: 'retrieve-saved-quote',
            // TODO: Implement retrieving a saved quote from Salesforce/EIS
            onClick: () => undefined,
          },
        ]
      : []),
    {
      label: 'Find an in-network dentist',
      href: 'https://www.guardianlife.com/find-a-dentist',
      dataTa: 'aside-find-dentist',
    },
    {
      label: 'Find an in-network optometrist',
      href: 'https://www.vspdirect.com/find-a-doctor',
      dataTa: 'aside-find-optometrist',
    },
    ...(pathname !== '/request-appointment' && seismicLink
      ? [
          {
            label: 'Important broker materials',
            href: seismicLink,
            dataTa: 'aside-broker-materials',
          },
        ]
      : []),
    ...(pathname !== '/request-appointment' && showRequestAdditionalStates
      ? [
          {
            label: 'Request additional states',
            href: '/request-states',
            dataTa: 'aside-request-states',
          },
        ]
      : []),
  ];

  const quickLinkContentClass = '!w-full !justify-start font-bold h-[39px] !text-blue-900 !no-underline hover:!no-underline active:!no-underline visited:!text-olive-800';

  const quickLinkArrowClass = '!ml-auto [&_svg]:!h-[11px] [&_svg]:!w-[12px]';

  return (
    <aside tabIndex={0} className="flex flex-col gap-16">
      <Card
        background="Olive"
        type="promotional"
        className="-mx-5 mb-8 md:mx-0 lg:my-0"
      >
        <>
          <CardHeader
            className="heading-xs"
            elementProps={{
              headerLabel: {
                className: '!font-effra',
              },
            }}
          >
            Quick Links
          </CardHeader>
          <CardContent className="!m-0 !p-0">
            <ul className="w-full list-none pt-4">
              {quickLinks.map((item) => (
                <li
                  key={item.dataTa}
                  className="w-full pb-3 last:pb-0"
                >
                  {item.href ? (
                      <Link
                        href={item.href}
                        className={quickLinkContentClass}
                        data-ta={item.dataTa}
                      >
                        {item.label}
                        <Icon
                          icon={faArrowRight}
                          size={12}
                          color="Tomato"
                          currentColorFill
                          decorative
                          className={quickLinkArrowClass}
                        />
                      </Link>
                  ) : (
                    <Button
                      variant="link"
                      className={quickLinkContentClass}
                      data-ta={item.dataTa}
                      onClick={item.onClick}
                    >
                      {item.label}
                      <Icon
                        icon={faArrowRight}
                        size={12}
                        color="Tomato"
                        currentColorFill
                        decorative
                        className={quickLinkArrowClass}
                      />
                    </Button>
                  )}
                  <Divider
                    orientation="horizontal"
                    className="mt-3 !border-olive-100"
                  />
                </li>
              ))}
            </ul>
          </CardContent>
        </>
      </Card>
    </aside>
  );
}
